import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, getDocs, limit, where, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { cn } from '../../utils/tw';
import { toast } from 'react-toastify';

// Smart title case logic (used for display and add)
function smartTitleCase(str: string): string {
  return str.split(' ').map(word => {
    if (word === word.toUpperCase()) return word; // keep all caps
    if (word.length === 0) return word;
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join(' ');
}

export interface PartyData {
  name: string;
  type: 'shop' | 'vendor' | 'office' | 'labourGroup';
  id?: string;
}

interface DynamicPartyInputProps {
  nature: string;
  value: string;
  onChange: (value: string) => void;
  onPartyChange?: (party: PartyData) => void; // New callback for unified party system
  disabled?: boolean;
  error?: string;
  required?: boolean;
  hideInternalLabel?: boolean;
}

// Configuration mapping for different natures
const PARTY_CONFIG = {
  routine: { label: 'Shop Name', collection: 'shops', type: 'shop' as const, placeholder: 'Select or add shop...' },
  stock: { label: 'Vendor Name', collection: 'vendors', type: 'vendor' as const, placeholder: 'Select or add vendor...' },
  capital: { label: 'Vendor Name', collection: 'vendors', type: 'vendor' as const, placeholder: 'Select or add vendor...' },
  'admin-finance': { label: 'Office/Bank', collection: 'offices', type: 'office' as const, placeholder: 'Select or add office/bank...' },
  payroll: { label: 'Labour Group', collection: 'labourGroups', type: 'labourGroup' as const, placeholder: 'Select or add labour group...' },
  maintenance: { label: 'Vendor Name', collection: 'vendors', type: 'vendor' as const, placeholder: 'Select or add vendor...' }
};

export const DynamicPartyInput: React.FC<DynamicPartyInputProps> = ({
  nature,
  value,
  onChange,
  onPartyChange,
  disabled = false,
  error,
  required = false,
  hideInternalLabel = false
}) => {
  const [suggestions, setSuggestions] = useState<Array<{name: string, id: string}>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  const config = PARTY_CONFIG[nature as keyof typeof PARTY_CONFIG] ||
                 PARTY_CONFIG.routine; // Default to routine config

  const loadSuggestions = useCallback(async (searchTerm: string = '', forceRefresh: boolean = false) => {
    // Don't refetch if we just fetched recently (unless forced)
    const now = Date.now();
    if (!forceRefresh && suggestions.length > 0 && now - lastFetchTime < 5000) {
      // Use cached suggestions if we fetched within the last 5 seconds
      if (searchTerm.trim()) {
        const filtered = suggestions
          .filter(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()))
          .slice(0, 10);
        setSuggestions(filtered);
      }
      return;
    }

    setIsLoading(true);
    try {
      const q = query(
        collection(db, config.collection),
        limit(50) // Increase limit to get more results
      );

      const snapshot = await getDocs(q);
      const items = snapshot.docs
        .map(doc => {
          const data = doc.data();
          const name = data.name || data.shopName || data.vendorName || data.officeName;
          if (!name) return null;
          return {
            name: smartTitleCase(name),
            id: doc.id
          };
        })
        .filter((item): item is {name: string, id: string} => item !== null)
        .sort((a, b) => a.name.localeCompare(b.name));

      setLastFetchTime(now);

      // Filter client-side if there's a search term
      if (searchTerm.trim()) {
        const filtered = items
          .filter(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()))
          .slice(0, 10);
        setSuggestions(filtered);
      } else {
        // Show all results if no search term
        setSuggestions(items.slice(0, 10));
      }
    } catch (error) {
      console.warn(`Error loading suggestions from ${config.collection}:`, error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [config.collection, suggestions, lastFetchTime]);

  // Debounce the search
  useEffect(() => {
    if (!isTyping) return;
    
    const timeoutId = setTimeout(() => {
      loadSuggestions(value);
      setIsTyping(false);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [value, isTyping, loadSuggestions]);

  const handleInputChange = (newValue: string) => {
    onChange(newValue);
    setIsTyping(true);
    setSelectedIndex(-1);
    setShowSuggestions(true); // Always show suggestions when typing
  };

  const handleSuggestionClick = (suggestion: {name: string, id: string}) => {
    onChange(suggestion.name);
    if (onPartyChange) {
      onPartyChange({
        name: suggestion.name,
        type: config.type,
        id: suggestion.id
      });
    }
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);
  };

  const handleAddNew = async () => {
    if (!value.trim()) return;
    const trimmedValue = value.trim();
    const formattedValue = smartTitleCase(trimmedValue);

    // Check if already exists
    const exists = suggestions.some(s => s.name.toLowerCase() === formattedValue.toLowerCase());
    if (exists) {
      toast.warn(`"${formattedValue}" already exists. Please select it from the list.`);
      return;
    }

    try {
      setIsLoading(true);
      const docRef = await addDoc(collection(db, config.collection), {
        name: formattedValue,
        createdAt: new Date(),
        createdBy: 'user' // You can replace with actual user info
      });

      // Call onPartyChange with the new party data
      if (onPartyChange) {
        onPartyChange({
          name: formattedValue,
          type: config.type,
          id: docRef.id
        });
      }

      toast.success(`Added "${formattedValue}" successfully!`);
      setShowSuggestions(false);
      setSuggestions([]);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Error adding new entry:', error);
      toast.error('Failed to add new entry');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions) return;

    const totalOptions = suggestions.length + (shouldShowAdd() ? 1 : 0);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % totalOptions);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev <= 0 ? totalOptions - 1 : prev - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          if (selectedIndex < suggestions.length) {
            handleSuggestionClick(suggestions[selectedIndex]);
          } else {
            handleAddNew();
          }
        } else if (shouldShowAdd()) {
          handleAddNew();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const shouldShowAdd = () => {
    const trimmedValue = value.trim();
    return trimmedValue.length > 0 &&
           !suggestions.some(s => s.name.toLowerCase() === trimmedValue.toLowerCase());
  };

  const handleFocus = () => {
    // Always force refresh on focus to get latest data
    loadSuggestions(value, true);
    setShowSuggestions(true);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, 200);
  };

  return (
    <div className={hideInternalLabel ? "relative" : "space-y-1"}>
      <div className="relative">
        <input
          id={`party-input-${nature}`}
          type="text"
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={hideInternalLabel ? config.placeholder : " "}
          className={hideInternalLabel ? cn(
            'w-full border border-gray-300 bg-white px-3 py-2.5 rounded-lg outline-none focus:border-info-500 focus:ring-2 focus:ring-info-500/20 text-sm transition-colors',
            error && 'border-error-500 focus:border-error-500 focus:ring-red-500/20',
            disabled && 'bg-gray-50 text-gray-400 cursor-not-allowed'
          ) : cn(
            'peer w-full border bg-card px-3 py-2 rounded-lg outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 placeholder-transparent min-h-[48px] transition-colors',
            error && 'border-error focus:border-error focus:ring-error/30',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          autoComplete="off"
        />
        
        {!hideInternalLabel && (
          <label
            htmlFor={`party-input-${nature}`}
            className="absolute left-3 top-1 text-xs text-mutedText transition-all peer-focus:-top-2 peer-focus:text-xs peer-focus:text-primary peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-mutedText"
          >
            {config.label} {required && <span className="text-error">*</span>}
          </label>
        )}
        
        {/* Loading indicator */}
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
          </div>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && (suggestions.length > 0 || shouldShowAdd()) && (
        <div className="absolute z-50 min-w-[260px] max-w-[340px] bg-card border rounded-lg shadow-card mt-1 max-h-40 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.id}
              className={cn(
                "p-3 cursor-pointer text-sm border-b border-border last:border-b-0 transition-colors",
                selectedIndex === index
                  ? "bg-primary text-white"
                  : "hover:bg-hover"
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSuggestionClick(suggestion);
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="font-medium">{suggestion.name}</div>
              <div className={cn(
                "text-xs",
                selectedIndex === index ? "text-white/70" : "text-mutedText"
              )}>
                From {config.collection}
              </div>
            </div>
          ))}
          
          {/* Add new option */}
          {shouldShowAdd() && (
            <div
              className={cn(
                "p-3 cursor-pointer text-sm border-t border-border transition-colors",
                selectedIndex === suggestions.length
                  ? "bg-primary/20 text-primary" 
                  : "hover:bg-primary/10 text-primary"
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                handleAddNew();
              }}
              onMouseEnter={() => setSelectedIndex(suggestions.length)}
            >
              <div className="font-medium flex items-center">
                <span className="mr-2 text-lg">+</span>
                Add "{smartTitleCase(value.trim())}"
              </div>
              <div className={cn(
                "text-xs",
                selectedIndex === suggestions.length ? "text-primary/70" : "text-mutedText"
              )}>
                Create new {config.label.toLowerCase()}
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <p className={hideInternalLabel ? "text-xs text-error-600 mt-1" : "text-error text-xs animate-fadeIn"}>{error}</p>
      )}
      
      {!hideInternalLabel && (
        <div className="text-xs text-mutedText">
          Type to search existing {config.label.toLowerCase()}s or add new with "+" button
        </div>
      )}
    </div>
  );
};

export default DynamicPartyInput;
